import SectionHeader from "@/components/SectionHeader";
import { Users, ShieldCheck, DollarSign, Construction, Sparkles, CalendarHeart, CheckCircle2, Building2 } from "lucide-react";

const ACHIEVE_IMG = "https://customer-assets.emergentagent.com/job_linda-burke/artifacts/c3d6uz3f_IMG_7940.jpeg";

const cards = [
  {
    icon: Users,
    title: "Strengthening City Staff & Leadership",
    color: "primary",
    points: [
      "Increased staffing to match South Lebanon's growth from a village of 2,500 to a city of 7,000 — addressing years of being understaffed.",
      "Hired a dedicated Code Enforcement Officer to improve the city's health and appearance.",
      "Onboarded a new City Administrator following the retirement of the former Administrator, plus a new Administrative Specialist.",
      "Created an atmosphere of transparency and trust — supporting department heads without interference.",
    ],
  },
  {
    icon: ShieldCheck,
    title: "Public Safety That Keeps Pace with Growth",
    color: "accent",
    points: [
      "Implemented a comprehensive traffic safety plan in partnership with the Warren County Sheriff's Department.",
      "Continuously evaluating future public safety positions and adjusting for our growing city population.",
    ],
  },
  {
    icon: DollarSign,
    title: "Award-Winning Financial Stewardship",
    color: "primary",
    points: [
      "Awarded one of the highest honors from the Ohio State Auditor's Office for clean audits — twice in the last two years.",
      "Sound budget management and full transparency with taxpayer dollars.",
      "Department of Finance recognized as one of the best in the region.",
    ],
  },
  {
    icon: Construction,
    title: "Infrastructure: Roads, Water & Sewer",
    color: "accent",
    points: [
      "Implemented a five-year paving plan for major thoroughfares.",
      "Paved more streets in the first 18 months than had been paved in the previous 10 years combined — a fact confirmed by the city's former administrator.",
      "Four additional streets included in this year's budget, including a new storm-water system installation to fix a recurring road-failure issue.",
      "Water Tower rehabilitation and painting project scheduled for summer 2026.",
    ],
  },
  {
    icon: Building2,
    title: "Expediting Long-Stalled Projects",
    color: "primary",
    points: [
      "Restarted the $11 million River Corridor Sewer Project — left on the back burner since 2018 — now critical to the long-term health of South Lebanon's sewer system.",
      "Implemented the $800,000 HUD grant project for the Community Center: roof, parking lot, and window upgrades.",
      "Began River Corridor development discussions — anchored by the first economic-development partner, South Lebanon River Dogs paddle company.",
    ],
  },
  {
    icon: CalendarHeart,
    title: "Family-Focused Community Events",
    color: "accent",
    points: [
      "Joint parades with Union Township and official wreath placement ceremonies at the Veterans Memorial.",
      "Movie Nights in the Park, the annual 4th of July event, and National Night Out.",
      "Annual Christmas Tree Lighting featuring Santa and his Elves.",
      "Launching neighborhood outreach programs to listen directly to residents in every part of the city.",
    ],
  },
  {
    icon: Sparkles,
    title: "A Modern, Inclusive City Identity",
    color: "primary",
    points: [
      "New City seal, logo, and website launching soon — designed to represent both South Lebanon's history and its growing community of new residents.",
    ],
  },
];

const stats = [
  { value: "2024", label: "Took Office as Mayor" },
  { value: "2×", label: "Clean Audit Awards" },
  { value: "$11M", label: "Sewer Project Restarted" },
  { value: "$800K", label: "HUD Grant Delivered" },
];

export default function Achievements() {
  return (
    <div>
      {/* HERO */}
      <section className="relative border-b border-border overflow-hidden">
        <div className="absolute inset-0 bg-cover bg-center opacity-20" style={{ backgroundImage: `url(${ACHIEVE_IMG})` }} aria-hidden />
        <div className="absolute inset-0 hero-overlay" aria-hidden />
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32">
          <div className="flex items-center gap-3 mb-5">
            <div className="flag-rule"><span /><span /><span /></div>
            <span className="text-xs uppercase tracking-[0.22em] font-bold text-accent">
              The Record
            </span>
          </div>
          <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl font-black text-primary leading-[0.95] tracking-tight max-w-4xl">
            Delivering Results <br /> for South Lebanon.
          </h1>
          <p className="mt-6 text-xl text-foreground/85 max-w-2xl leading-relaxed">
            Real projects. Real progress. Real results — for the families, businesses, and neighbors who call South Lebanon home.
          </p>
        </div>
      </section>

      {/* STATS */}
      <section className="bg-primary text-primary-foreground">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-12 grid grid-cols-2 md:grid-cols-4 divide-y md:divide-y-0 md:divide-x divide-primary-foreground/15">
          {stats.map((s) => (
            <div key={s.label} className="text-center py-4 md:py-0 px-4" data-testid={`stat-${s.label.toLowerCase().replace(/[^a-z]/g, "-")}`}>
              <div className="font-serif text-4xl md:text-5xl font-black text-accent">{s.value}</div>
              <div className="mt-2 text-xs uppercase tracking-[0.2em] text-primary-foreground/80 font-bold">{s.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* GRID */}
      <section className="py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <SectionHeader
            eyebrow="Accomplishments"
            title="A Record of Action."
            intro="From staffing to streets, from finance to family events — here's what Mayor Burke and her team have delivered for South Lebanon."
          />
          <div className="grid grid-cols-1 md:grid-cols-12 gap-6" data-testid="achievements-grid">
            {cards.map((c, i) => {
              const Icon = c.icon;
              // bento spans — 7 cards
              const spans = [
                "md:col-span-7", "md:col-span-5",
                "md:col-span-5", "md:col-span-7",
                "md:col-span-6", "md:col-span-6",
                "md:col-span-12",
              ];
              return (
                <article
                  key={c.title}
                  data-testid="achievement-card"
                  className={`${spans[i]} bg-card border border-border p-8 md:p-10 relative`}
                >
                  <div className={`absolute top-0 left-0 right-0 h-1 ${c.color === "accent" ? "bg-accent" : "bg-primary"}`} aria-hidden />
                  <div className="flex items-start gap-5 mb-5">
                    <div className={`w-14 h-14 shrink-0 flex items-center justify-center ${c.color === "accent" ? "bg-accent" : "bg-primary"} text-primary-foreground`}>
                      <Icon size={26} />
                    </div>
                    <div>
                      <div className="text-xs uppercase tracking-[0.22em] font-bold text-muted-foreground">Focus Area #{i + 1}</div>
                      <h3 className="font-serif text-2xl md:text-3xl font-bold text-primary mt-1 leading-tight">{c.title}</h3>
                    </div>
                  </div>
                  <ul className="space-y-3 mt-5">
                    {c.points.map((p) => (
                      <li key={p} className="flex gap-3 text-foreground/85 leading-relaxed">
                        <CheckCircle2 size={20} className={`shrink-0 mt-0.5 ${c.color === "accent" ? "text-accent" : "text-primary"}`} />
                        <span>{p}</span>
                      </li>
                    ))}
                  </ul>
                </article>
              );
            })}
          </div>

          {/* Public Works callout */}
          <div className="mt-12 bg-primary text-primary-foreground p-8 md:p-12 relative">
            <div className="flag-rule mb-5"><span /><span /><span /></div>
            <h3 className="font-serif text-3xl md:text-4xl font-bold leading-tight max-w-3xl">
              "Our Public Works staff is outstanding — the backbone of our city. There is nothing they can't do. They have my highest respect."
            </h3>
            <div className="mt-5 text-sm uppercase tracking-[0.22em] text-primary-foreground/80 font-bold">
              — Mayor Linda Burke
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
